import type { User } from 'firebase/auth'
import { fetchAuthenticatedUserBasicResult } from '@/features/user/api'

export type AuthState = 'notLoggedIn' | 'verifyEmail' | 'needsProfile' | 'ready' | 'error'

export interface ResolvedAuthState {
  state: AuthState
  currentUser: Awaited<ReturnType<typeof fetchAuthenticatedUserBasicResult>>['userWithStats'] | null
  errorMessage: string | null
  shouldSignOut: boolean
}

const AUTH_RETRY_DELAYS = [350, 900, 1500]

function isEmailPasswordUser(user: User) {
  return user.providerData.some((provider) => provider.providerId === 'password')
}

function isNetworkError(error: unknown) {
  return error instanceof TypeError
}

function getRetryDelay(attempt: number) {
  return AUTH_RETRY_DELAYS[Math.min(attempt, AUTH_RETRY_DELAYS.length - 1)]!
}

function wait(delayMs: number) {
  return new Promise((resolve) => globalThis.setTimeout(resolve, delayMs))
}

export async function resolveAuthState(user: User | null): Promise<ResolvedAuthState> {
  if (!user) {
    return {
      state: 'notLoggedIn',
      currentUser: null,
      errorMessage: null,
      shouldSignOut: false,
    }
  }

  if (isEmailPasswordUser(user) && !user.emailVerified) {
    return {
      state: 'verifyEmail',
      currentUser: null,
      errorMessage: null,
      shouldSignOut: false,
    }
  }

  for (let attempt = 0; attempt < AUTH_RETRY_DELAYS.length; attempt += 1) {
    const isLastAttempt = attempt === AUTH_RETRY_DELAYS.length - 1

    let token: string | null
    try {
      // Only the verify-email flow should force-refresh the Firebase token.
      // The normal auth gate should reuse the current token to avoid refresh loops.
      token = await user.getIdToken()
    } catch (error) {
      if (isLastAttempt) {
        return {
          state: 'notLoggedIn',
          currentUser: null,
          errorMessage: null,
          shouldSignOut: true,
        }
      }

      await wait(getRetryDelay(attempt))
      continue
    }

    if (!token) {
      return {
        state: 'notLoggedIn',
        currentUser: null,
        errorMessage: null,
        shouldSignOut: true,
      }
    }

    try {
      const result = await fetchAuthenticatedUserBasicResult(token)

      if (result.isProfileComplete === true) {
        return {
          state: 'ready',
          currentUser: result.userWithStats,
          errorMessage: null,
          shouldSignOut: false,
        }
      }

      if (result.isProfileComplete === false) {
        return {
          state: 'needsProfile',
          currentUser: result.userWithStats,
          errorMessage: null,
          shouldSignOut: false,
        }
      }

      if (!isLastAttempt) {
        await wait(getRetryDelay(attempt))
        continue
      }

      return {
        state: 'error',
        currentUser: result.userWithStats,
        errorMessage: 'We konden je profielstatus niet bepalen.',
        shouldSignOut: false,
      }
    } catch (error) {
      if (
        typeof error === 'object' &&
        error !== null &&
        'isExplicitUserMissing' in error &&
        (error as { isExplicitUserMissing?: boolean }).isExplicitUserMissing
      ) {
        return {
          state: 'needsProfile',
          currentUser: null,
          errorMessage: null,
          shouldSignOut: false,
        }
      }

      const status =
        typeof error === 'object' && error !== null && 'status' in error
          ? (error as { status?: number }).status
          : null

      if (status === 401 || status === 403) {
        return {
          state: 'notLoggedIn',
          currentUser: null,
          errorMessage: null,
          shouldSignOut: true,
        }
      }

      if ((status === 429 || (typeof status === 'number' && status >= 500) || isNetworkError(error)) && !isLastAttempt) {
        await wait(getRetryDelay(attempt))
        continue
      }

      if (status === 429) {
        return {
          state: 'error',
          currentUser: null,
          errorMessage: 'Te veel verzoeken. Probeer het straks opnieuw.',
          shouldSignOut: false,
        }
      }

      if (typeof status === 'number' && status >= 500) {
        return {
          state: 'error',
          currentUser: null,
          errorMessage: 'De server is tijdelijk niet beschikbaar.',
          shouldSignOut: false,
        }
      }

      if (isNetworkError(error)) {
        return {
          state: 'error',
          currentUser: null,
          errorMessage: 'Er is geen verbinding met de server.',
          shouldSignOut: false,
        }
      }

      return {
        state: 'error',
        currentUser: null,
        errorMessage: 'We konden je account nu niet laden.',
        shouldSignOut: false,
      }
    }
  }

  return {
    state: 'error',
    currentUser: null,
    errorMessage: 'De accountcontrole is mislukt.',
    shouldSignOut: false,
  }
}
